import type { Answers } from "@/schema/quizSchema";
import { BRAND_LABEL, type Verdict } from "./copy";

export type RootCause = { name: string; copy: string };
export type ChemLoad = { level: "HIGH"; copy: string };

export interface ManlyProfile {
  verdict: Verdict;
  primary: RootCause;
  secondary: RootCause;
  chem: ChemLoad;
  embedded: boolean;
  skinIssue: boolean;
  clothes: boolean;
  mood: boolean;
  brandLabel: string;
  assessment: string;
}

const list = (v: unknown): string[] => (Array.isArray(v) ? (v as string[]) : []);
const str = (v: unknown): string => (typeof v === "string" ? v : "");

/**
 * Pure. Maps a completed answer set to the full odor profile.
 *
 * Mood/energy (moody, tired) deliberately do NOT feed the verdict — they feed
 * the chemical-load thread only. Absolution framing: we never claim a body wash
 * causes or cures mood.
 */
export function deriveProfile(answers: Answers): ManlyProfile {
  const where = list(answers["where-it-shows"]);
  const beyond = list(answers["beyond-smell"]);
  const brand = str(answers["current-wash"]);
  const smell = str(answers["smell-returns"]);
  // Q "activity" is deliberately not read here any more: the kit routing was
  // its only consumer. The question stays in the quiz (it earns its place in
  // the diagnostic and ships in the answer payload), it just no longer feeds
  // the profile.

  const skinIssue = beyond.some((v) => ["breakouts", "dry", "rash"].includes(v));
  const mood = beyond.includes("moody") || beyond.includes("tired");
  const clothes = where.includes("clothes");

  const embedded =
    ["hour", "schoolday", "never"].includes(smell) || where.length >= 3 || clothes;
  const verdict: Verdict = embedded && skinIssue ? "COMPOUNDING" : embedded ? "EMBEDDED" : "SURFACE";

  const brandLabel = BRAND_LABEL[brand] ?? "his current wash";

  // Primary root cause: the mechanism of the brand he uses today.
  const primary: RootCause =
    brand === "natural"
      ? {
          name: "Essential-oil fade-out",
          copy: "Natural masking: the scent covers odor, then fades by midday. The bacteria were never killed.",
        }
      : {
          name: "Masking, not killing",
          copy: "Synthetic fragrance covers the odor for about an hour. The bacteria survive underneath.",
        };

  // Secondary: the strongest symptom, with a backfill so the grid never has holes.
  let secondary: RootCause;
  if (clothes) {
    secondary = {
      name: "Fabric reservoir",
      copy: "Surviving bacteria re-seed his shirts. Washing doesn't clear it.",
    };
  } else if (beyond.includes("rash")) {
    secondary = {
      name: "Baking-soda rash cycle",
      copy: "The classic natural-stick failure: alkaline baking soda on teen skin.",
    };
  } else if (beyond.includes("dry") || beyond.includes("breakouts")) {
    secondary = {
      name: "Stripped skin barrier",
      copy: "SLS strips the barrier, which drives the dryness, irritation and breakout cycle.",
    };
  } else if (mood) {
    secondary = {
      name: "Endocrine-disruptor load",
      copy: "Daily synthetic exposure (fragrance, SLS, parabens) during the exact years his hormones are calibrating.",
    };
  } else {
    secondary = {
      name: "3x bacteria load",
      copy: "Puberty chemistry makes about 3x more odor-causing bacteria than adult men. Adult formulas are outgunned.",
    };
  }

  // Second thread: the daily chemical load. Always HIGH — every mainstream wash
  // puts synthetic fragrance and SLS on his skin daily, so the stat never reads
  // as a blank "UNKNOWN".
  const chem: ChemLoad = {
    level: "HIGH",
    copy: "Synthetic fragrance and SLS on his skin every shower, during the exact years his endocrine system is calibrating.",
  };

  // No kit routing: the funnel sells the body wash alone, so there is nothing
  // to route between. See offer.ts.

  const assessment =
    brand === "natural"
      ? `${brandLabel} masks odor with essential oils that fade by midday, and the bacteria stay alive on his skin${clothes ? " and in his clothes" : ""}.`
      : `${brandLabel} masks the smell for about an hour while the bacteria stay alive on his skin${clothes ? ", and by now in his clothes" : ""}.`;

  return {
    verdict, primary, secondary, chem,
    embedded, skinIssue, clothes, mood, brandLabel, assessment,
  };
}
